import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  useToast,
} from '@caruso/ui';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth-store';
import { Lock, Mail } from 'lucide-react';
import { DevEnvironmentBanner } from '@/components/dev-environment-banner';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const response = await authService.login(data);
      login(response.user, response.tokens.accessToken);
      toast({
        title: 'Login realizado com sucesso!',
        description: `Bem-vindo, ${response.user.name}!`,
        variant: 'success',
      });
      navigate('/app');
    } catch (error: any) {
      toast({
        title: 'Erro ao fazer login',
        description: error.response?.data?.message || 'Credenciais inválidas',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-smoke via-brand-ink to-brand-smoke flex items-center justify-center p-4">
      {/* Development Environment Banner */}
      <DevEnvironmentBanner />
      <div className="absolute inset-0 bg-noise opacity-20" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="bg-brand-ink/90 backdrop-blur-sm border-brand-gold/20 premium-border">
          <CardHeader className="text-center space-y-2">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="mx-auto w-20 h-20 rounded-full bg-brand-gold/10 flex items-center justify-center mb-4"
            >
              <Lock className="w-10 h-10 text-brand-gold" strokeWidth={1.5} />
            </motion.div>
            <CardTitle className="text-3xl font-serif text-brand-gold">Caruso CRM</CardTitle>
            <p className="text-brand-ivory/60 text-sm">Sistema Premium de Gestão de Clientes</p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-brand-ivory">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@caruso.local"
                    className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory placeholder:text-brand-ivory/30 focus:border-brand-gold"
                    {...register('email')}
                    disabled={isLoading}
                  />
                </div>
                {errors.email && <p className="text-xs text-red-400">{errors.email.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-brand-ivory">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory placeholder:text-brand-ivory/30 focus:border-brand-gold"
                    {...register('password')}
                    disabled={isLoading}
                  />
                </div>
                {errors.password && (
                  <p className="text-xs text-red-400">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-sm">
                <a href="#" className="text-brand-gold/70 hover:text-brand-gold transition-colors">
                  Esqueci minha senha
                </a>
              </div>

              <Button type="submit" className="w-full" variant="premium" disabled={isLoading}>
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-brand-ink border-t-transparent rounded-full"
                  />
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-xs text-brand-ivory/40">
              © 2025 TW Tecnologia. Todos os direitos reservados.
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
